import { BIOMES, type ItemSource, itemSources, MOBS, sourceBiome } from '@shefira/shared';

import { uiFormat, uiPercent, uiText } from './text.js';

/**
 * « Où je trouve ça ? » in the player's words, one line per source, each
 * behind the HUD icon of its kind (`icon-<name>.png`). The facts come from
 * `itemSources`, which reads the tables the server rolls.
 */

export interface SourceLine {
  icon: string;
  text: string;
}

/** Past this many creatures, the rest are counted rather than named. */
const MAX_CREATURES = 4;

/** « 30 % », « 0,5 % » (« 0.5% » in English): a drop chance. */
function percent(chance: number): string {
  return uiPercent(Number((chance * 100).toFixed(1)));
}

function region(source: ItemSource): string {
  const biome = sourceBiome(source);
  return biome === undefined ? '' : BIOMES[biome].name;
}

function sourceLine(source: ItemSource): SourceLine {
  switch (source.kind) {
    case 'mob':
      return {
        icon: 'stat-kills',
        text: uiFormat('{mob} · {region} ({chance})', {
          mob: MOBS[source.mobId].name,
          region: region(source),
          chance: percent(source.chance),
        }),
      };
    case 'guardian':
      return {
        icon: 'ach-camp',
        text: uiFormat('Gardiens des lieux d’intérêt · {region}', { region: region(source) }),
      };
    case 'elite':
      return { icon: 'ach-elite', text: uiFormat('Élites · {region}', { region: region(source) }) };
    case 'boss':
      return {
        icon: 'stat-bosses',
        text:
          source.chance === undefined
            ? uiFormat('{mob}, boss · {region}', {
                mob: MOBS[source.mobId].name,
                region: region(source),
              })
            : uiFormat('{mob}, boss · {region} ({chance})', {
                mob: MOBS[source.mobId].name,
                region: region(source),
                chance: percent(source.chance),
              }),
      };
    case 'chest':
      return source.campsOnly
        ? {
            icon: 'ach-camp',
            text: uiFormat('Coffres des camps · {region}', { region: region(source) }),
          }
        : {
            icon: 'ach-chest',
            text: uiFormat('Coffres cachés et des camps · {region}', { region: region(source) }),
          };
    case 'biome-loot':
      return {
        icon: 'stat-loot',
        text: uiFormat('Butin des monstres · {region}', { region: region(source) }),
      };
    case 'merchant':
      return { icon: 'merchant', text: uiText('Marchand des villages') };
    case 'alchemist':
      return {
        icon: 'alchemy',
        text: uiFormat('Alchimiste · {village}', { village: BIOMES[source.biome].village }),
      };
  }
}

/** The lines to show for this item; empty when nothing in the world gives it. */
export function sourceLines(templateId: string): SourceLine[] {
  const sources = itemSources(templateId);
  const creatures = sources.filter((source) => source.kind === 'mob');
  const lines = [
    ...creatures.slice(0, MAX_CREATURES),
    ...sources.filter((source) => source.kind !== 'mob'),
  ].map(sourceLine);
  const more = creatures.length - MAX_CREATURES;
  if (more > 0) {
    lines.splice(MAX_CREATURES, 0, {
      icon: 'stat-kills',
      text:
        more > 1
          ? uiFormat('… et {count} autres créatures', { count: more })
          : uiFormat('… et {count} autre créature', { count: more }),
    });
  }
  return lines;
}
